/**
 * InputEngine — microphone capture via AudioWorklet.
 *
 * Uses its own AudioContext (separate from OutputEngine) at the STT
 * provider's required sample rate (e.g. 16kHz for Cartesia ink-whisper).
 *
 * Architecture:
 *   getUserMedia → MediaStreamSource → AudioWorklet (raw-processor)
 *   → postMessage Int16 chunks to main thread → onAudio callback
 */

const MIC_SAMPLE_RATE = 16000;

export class InputEngine {
  constructor() {
    this._ctx = null;
    this._worklet = null;
    this._stream = null;
    this._source = null;
    this._active = false;
    this.onAudio = null; // callback: (Int16Array) => void
  }

  /**
   * Start mic capture.
   * @returns {Promise<boolean>} success
   */
  async start() {
    console.log('[InputEngine] start, active:', this._active);
    if (this._active) return true;

    try {
      // Get mic
      console.log('[InputEngine] requesting getUserMedia');
      this._stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: MIC_SAMPLE_RATE },
        },
      });

      // Create AudioContext at STT sample rate
      const AC = window.AudioContext || window.webkitAudioContext;
      this._ctx = new AC({ sampleRate: MIC_SAMPLE_RATE });

      // Load worklet
      const workletUrl = new URL('./worklets/raw-processor.js', import.meta.url);
      await this._ctx.audioWorklet.addModule(workletUrl);

      this._source = this._ctx.createMediaStreamSource(this._stream);
      this._worklet = new AudioWorkletNode(this._ctx, 'raw-processor');

      // Receive PCM chunks from worklet
      this._worklet.port.onmessage = (e) => {
        if (e.data.type === 'audio' && this.onAudio) {
          this.onAudio(e.data.data);
        }
      };

      // Connect: source → worklet (no destination — silent capture)
      this._source.connect(this._worklet);
      // Worklet must connect to destination to process (use silent gain)
      const silentGain = this._ctx.createGain();
      silentGain.gain.value = 0;
      this._worklet.connect(silentGain);
      silentGain.connect(this._ctx.destination);

      await this._ctx.resume();
      console.log('[InputEngine] started successfully');
      this._active = true;
      return true;
    } catch (err) {
      console.error('[InputEngine] start error:', err.message);
      this.stop();
      return false;
    }
  }

  /** Stop mic capture and clean up. */
  stop() {
    this._active = false;

    if (this._stream) {
      this._stream.getTracks().forEach(t => t.stop());
      this._stream = null;
    }
    if (this._source) {
      this._source.disconnect();
      this._source = null;
    }
    if (this._worklet) {
      this._worklet.disconnect();
      this._worklet = null;
    }
    if (this._ctx) {
      this._ctx.close().catch(() => {});
      this._ctx = null;
    }
  }

  /** Whether mic is currently active. */
  get active() { return this._active; }
}
